import type { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { Spinner, SpinnerSize } from '@fluentui/react';
import { useAuth } from '@auth';

interface ProtectedRouteProps {
  children: ReactNode;
  requiredRoles?: string[];
  authorizedUpns?: string[];
  redirectTo?: string;
}

/**
 * ProtectedRoute component that requires authentication
 * Optionally checks for specific roles or UPNs
 */
export function ProtectedRoute({ 
  children, 
  requiredRoles = [], 
  authorizedUpns = [],
  redirectTo = '/login' 
}: ProtectedRouteProps) {
  const { isAuthenticated, isLoading, user } = useAuth();

  // Show loading spinner while checking authentication
  if (isLoading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh' 
      }}>
        <Spinner size={SpinnerSize.large} label="Authenticating..." />
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to={redirectTo} replace />;
  }

  // Check role-based or UPN-based access if required
  if ((requiredRoles.length > 0 || authorizedUpns.length > 0) && user) {
    const hasRequiredRole = requiredRoles.length === 0 || requiredRoles.some(role => 
      user.roles?.includes(role)
    );
    
    const hasAuthorizedUpn = authorizedUpns.length === 0 || authorizedUpns.some(upn =>
      user.username?.toLowerCase() === upn.toLowerCase()
    );

    // User needs either a required role OR an authorized UPN
    if (!hasRequiredRole && !hasAuthorizedUpn) {
      return <Navigate to="/unauthorized" replace />;
    }
  }

  return <>{children}</>;
}

export default ProtectedRoute;
